import React, { type FC, forwardRef, type ReactNode } from "react";
import classNames from "classnames";
import CircleSpinner from "./CircleSpinner/CircleSpinner";
import { LinkButton } from "./Link";
import { Dialog, DialogActions, DialogContent, type DialogProps, Slide, type TransitionProps } from "~/common/mui";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { C_SECONDARY_CONTAINER } from "./icon/Colors";
import { useNumericScreenSize } from "../hooks/useNumericScreenSize";
import { t } from "i18next";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    // This component is deprecated anyway
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export interface ModalProps {
  children?: ReactNode;
  title?: string;
  onClose: (e: React.MouseEvent) => void;
  size?: ModalSize;
  id?: string;
  modalClassName?: string;
  modalBgClassName?: string;
  modalContentClassName?: string;
  footerChildren?: ReactNode;
  isLoading?: boolean;
  noPadding?: boolean;
  hideClose?: boolean;
  footerClassName?: string;
  headerOverride?: ReactNode;
  rootProps?: JSX.IntrinsicElements["div"] & Partial<Record<GAAttributes, string>>;
}

type GAAttributes = "clta-modal-parent-marker" | "clta-product-name" | "clta-product-id" | "clta-total-value";

/**
 * @deprecated use Mui's Dialog instead
 */
const Modal = forwardRef<HTMLDivElement, ModalProps>((props, ref) => {
  const {
    title,
    modalClassName,
    modalBgClassName,
    modalContentClassName,
    size = ModalSize.MEDIUM,
    onClose,
    isLoading,
    noPadding,
    footerChildren,
    id,
    hideClose,
    footerClassName,
    headerOverride,
    rootProps,
    children,
  } = props;

  const screenSize = useNumericScreenSize();
  const isMobile = screenSize <= useNumericScreenSize.phone;

  const modalClasses = classNames(modalClassName, modalBgClassName);

  const contentCls = classNames("scha__modal__contents", {
    "scha__modal--no-padding": noPadding,
    "scha__modal__contents--loading": isLoading,
  });
  const headerCls = classNames("scha__modal__header", {
    "scha__modal__header--icon-flex-end": !title,
  });

  const spinnerCls = classNames("scha__modal__contents__spinner", {
    "scha__modal__contents__spinner--invisible": !isLoading,
  });

  const handleClose: DialogProps["onClose"] = (e: React.MouseEvent<HTMLAnchorElement>, reason) => {
    e.preventDefault();
    e.stopPropagation();
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return; // Prevent closing on outside click or escape key
    }
    onClose(e);
  };

  const getCustomWidth = () => {
    switch (size) {
      case ModalSize.LARGE:
        return "1200px";
      case ModalSize.MEDIUM:
        return "980px";
      case ModalSize.SMALL:
        return "780px";
      case ModalSize.TABLET:
        return "720px";
    }
  };

  return (
    <Dialog
      ref={ref}
      open={true}
      onClose={handleClose}
      slots={{
        transition: isMobile ? Transition : undefined,
      }}
      fullScreen={isMobile}
      slotProps={{
        paper: {
          className: modalClasses,
          sx: (theme) => ({
            maxWidth: getCustomWidth(),
            [theme.breakpoints.down("tablet")]: {
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              maxHeight: "100vh",
              position: "fixed",
              margin: 0,
              bottom: 0,
              left: 0,
              right: 0,
            },
          }),
        },
      }}
    >
      {headerOverride ??
        (!(!title && hideClose) && (
          <div className={classNames(headerCls)}>
            {title && <ModalTitle>{title}</ModalTitle>}
            {!hideClose && (
              <LinkButton
                onClick={onClose}
                aria-label={t("close")}
                id={id}
                style={{
                  backgroundColor: C_SECONDARY_CONTAINER,
                  borderRadius: "50%",
                  alignSelf: "flex-start",
                }}
              >
                <CloseRoundedIcon fontSize="medium" color="secondary" />
              </LinkButton>
            )}
          </div>
        ))}

      <DialogContent
        className={classNames(contentCls, modalContentClassName)}
        sx={{
          paddingTop: !title && hideClose ? "30px" : "0px",
        }}
      >
        <div className={spinnerCls}>
          <CircleSpinner />
        </div>
        <div className="scha__modal__contents__children">{!isLoading && children}</div>
      </DialogContent>
      {footerChildren && <DialogActions className={classNames(footerClassName)}>{footerChildren}</DialogActions>}
    </Dialog>
  );
});

export enum ModalSize {
  LARGE,
  MEDIUM,
  SMALL,
  TABLET,
}

export default Modal;

export const ModalTitle: FC<JSX.IntrinsicElements["div"]> = ({ className, ...rest }) => (
  <div className={classNames("scha__modal__title", className)} {...rest} />
);

export const ModalFooter: FC<JSX.IntrinsicElements["div"]> = ({ className, ...rest }) => (
  <div className={classNames("scha__modal__footer", className)} {...rest} />
);
